import React, { FC, useCallback, useEffect, useState } from 'react'
import { Redirect, Route, Switch } from 'react-router'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { CrmAccessTokenPayloadDto } from '@sparling/financial-common'
import { useAuth, useRest } from './components/Context'
import { routes } from './constants/routes'
import { GeneralLayout } from './components/Layouts'
import { Login } from './pages/Login'
import { RequestResetPassword, ResetPassword } from './pages/ResetPasswordFlow'
import { Home } from './pages/Home'
import { CompaniesRoutes } from './pages/Companies/Routes'
import { Trading } from './pages/Trading'
import { UsersRoutes } from './pages/Users/Routes'
import { SettingsGeneralRoutes } from './pages/Settings/General/Routes'
import { SettingsAssetsRoutes } from './pages/Settings/Assets/Routes'
import { SettingsBusinessEntityRoutes } from './pages/Settings/BusinessEntities/Routes'
import { FinanceRoutes } from './pages/Finance/Routes'
import { UserActiveSession } from './components/UserActiveSession'
import { SettingsRulesRoutes } from './pages/Settings/Rules/Routes'
import { SettingsAdminUsersRoutes } from './pages/Settings/AdminUsers/Routes'

export const RootRoutes: FC = () => {
  const { isUserLoggedIn, getAccessTokenData } = useAuth()
  const { rest } = useRest()
  const location = useLocation()
  const [redirectTo, setRedirectTo] = useState<string>()

  const first = window.location.pathname

  useEffect(() => {
    if (first !== '/' && first !== '/login') {
      setRedirectTo(first)
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (window.location.pathname === redirectTo) {
      setRedirectTo('')
    }
    // eslint-disable-next-line
  }, [window.location.pathname])

  const getAuthData = useCallback(async () => {
    try {
      const tokenData: CrmAccessTokenPayloadDto | undefined = getAccessTokenData()
      if (tokenData) {
        window.location.replace(location.pathname)
      }
      // eslint-disable-next-line no-empty
    } catch (err) {}
    // eslint-disable-next-line
  }, [rest, location.pathname])

  useEffect(() => {
    if (!isUserLoggedIn) {
      ;(async () => {
        await getAuthData()
      })()
    }
    // eslint-disable-next-line
  }, [isUserLoggedIn])

  return (
    <BrowserRouter>
      {!isUserLoggedIn ? (
        <Switch>
          <Route path={routes.LOGIN} exact component={Login} />
          <Route path={routes.REQUEST_RESET_PASSWORD} exact component={RequestResetPassword} />
          <Route path={routes.RESET_PASSWORD} exact component={ResetPassword} />
          <Redirect to={routes.LOGIN} />
        </Switch>
      ) : (
        <UserActiveSession>
          <GeneralLayout>
            <Switch>
              <Route path={routes.HOME} exact component={Home} />
              <Route path={routes.COMPANIES} component={CompaniesRoutes} />
              <Route path={routes.USERS} component={UsersRoutes} />
              <Route path={routes.TRADING} exact component={Trading} />
              <Route path={routes.FINANCE} component={FinanceRoutes} />
              <Route path={routes.SETTINGS}>
                <Switch>
                  <Route path={routes.SETTINGS_GENERAL} component={SettingsGeneralRoutes} />
                  <Route path={routes.SETTINGS_ADMIN_USERS} component={SettingsAdminUsersRoutes} />
                  <Route path={routes.SETTINGS_ASSETS} component={SettingsAssetsRoutes} />
                  <Route path={routes.SETTINGS_ENTITIES} component={SettingsBusinessEntityRoutes} />
                  <Route path={routes.SETTINGS_RULES} component={SettingsRulesRoutes} />
                  <Redirect to={routes.SETTINGS_GENERAL} />
                </Switch>
              </Route>
              <Redirect to={redirectTo || routes.HOME} />
            </Switch>
          </GeneralLayout>
        </UserActiveSession>
      )}
    </BrowserRouter>
  )
}
